import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeft,
  Calendar,
  DollarSign,
  Users,
  Home,
  Download,
  Mail,
  PenTool,
  XCircle,
  MoreVertical,
  RefreshCw,
  AlertTriangle,
} from "lucide-react";
import { getLeaseById, signLease } from "@/lib/lease-service";
import { LeaseAgreement } from "@/types/types";
import { formatNaira } from "@/lib/utils";
import { toast } from "sonner";
import { useAuth } from "@/components/auth-provider";
import { getLeaseStatusVariant, formatLeaseStatus } from "@/lib/lease-utils";

export default function LeaseDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [lease, setLease] = useState<LeaseAgreement | null>(null);
  const [loading, setLoading] = useState(true);
  const [showSignDialog, setShowSignDialog] = useState(false);
  const [showTerminateDialog, setShowTerminateDialog] = useState(false);
  const [showSendEmailDialog, setShowSendEmailDialog] = useState(false);
  const [signing, setSigning] = useState(false);
  const [signature, setSignature] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [terminationReason, setTerminationReason] = useState("");

  useEffect(() => {
    loadLease();
  }, [id]);

  const loadLease = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const leaseData = await getLeaseById(id);
      setLease(leaseData);
    } catch (error) {
      console.error("Error loading lease:", error);
      toast.error("Failed to load lease details");
    } finally {
      setLoading(false);
    }
  };

  const handleSignLease = async () => {
    if (!id || !user?.id || !signature.trim()) {
      toast.error("Please provide your signature");
      return;
    }

    try {
      setSigning(true);
      await signLease(id, user.id, signature, "");
      toast.success("Lease signed successfully");
      setShowSignDialog(false);
      setSignature("");
      await loadLease(); // Reload to show updated signature status
    } catch (error: any) {
      console.error("Error signing lease:", error);
      toast.error(error.message || "Failed to sign lease");
    } finally {
      setSigning(false);
    }
  };

  const handleDownloadPDF = () => {
    toast.info(
      "PDF download will be implemented with a PDF generation library"
    );
    // In a real app, you would generate a PDF using jsPDF or similar
  };

  const handleSendEmail = () => {
    if (!emailMessage.trim()) {
      toast.error("Please enter a message");
      return;
    }
    toast.success("Email sent successfully to tenant");
    setShowSendEmailDialog(false);
    setEmailMessage("");
    // In a real app, this would call a cloud function to send email
  };

  const handleTerminateLease = () => {
    if (!terminationReason.trim()) {
      toast.error("Please provide a termination reason");
      return;
    }
    toast.success("Lease termination initiated");
    setShowTerminateDialog(false);
    setTerminationReason("");
    // In a real app, this would update the lease status
  };

  const handleRenewLease = () => {
    toast.info("Lease renewal will be implemented");
    // Navigate to renewal form or open renewal dialog
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Loading lease details...</p>
        </div>
      </div>
    );
  }

  if (!lease) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <p className="text-muted-foreground mb-4">Lease not found</p>
            <Button onClick={() => navigate("/landlord/leases")}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Leases
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate("/landlord/leases")}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Lease Agreement</h1>
            <p className="text-muted-foreground">Lease ID: {lease.id}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={getLeaseStatusVariant(lease.status)}>
            {formatLeaseStatus(lease.status)}
          </Badge>

          {/* Action Buttons */}
          <div className="flex gap-2">
            {/* Sign Button - Show only if pending signature and user is landlord */}
            {lease.status === "pending_signatures" &&
              !lease.landlordSigned &&
              user?.id === lease.landlordId && (
                <Button onClick={() => setShowSignDialog(true)}>
                  <PenTool className="h-4 w-4 mr-2" />
                  Sign Lease
                </Button>
              )}

            {/* More Actions Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                <DropdownMenuItem onClick={handleDownloadPDF}>
                  <Download className="h-4 w-4 mr-2" />
                  Download PDF
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setShowSendEmailDialog(true)}>
                  <Mail className="h-4 w-4 mr-2" />
                  Send to Email
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() =>
                    navigate(`/landlord/leases/${lease.id}/payments`)
                  }
                >
                  <DollarSign className="h-4 w-4 mr-2" />
                  Payment History
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {lease.status === "active" && (
                  <DropdownMenuItem onClick={handleRenewLease}>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Renew Lease
                  </DropdownMenuItem>
                )}
                {(lease.status === "active" ||
                  lease.status === "pending_signatures") && (
                  <DropdownMenuItem
                    onClick={() => setShowTerminateDialog(true)}
                    className="text-destructive focus:text-destructive"
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Terminate Lease
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Property</CardTitle>
            <Home className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {lease.propertyTitle ||
                `Property ${lease.propertyId.slice(0, 8)}`}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Tenant</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {lease.tenantName || `Tenant ${lease.tenantId.slice(0, 8)}`}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Monthly Rent</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatNaira(lease.monthlyRent)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Lease Period</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-sm font-medium">
              {new Date(lease.startDate).toLocaleDateString()} -{" "}
              {new Date(lease.endDate).toLocaleDateString()}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Lease Details */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Financial Terms</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Monthly Rent</span>
              <span className="font-medium">
                {formatNaira(lease.monthlyRent)}
              </span>
            </div>
            <Separator />
            <div className="flex justify-between">
              <span className="text-muted-foreground">Security Deposit</span>
              <span className="font-medium">
                {formatNaira(lease.securityDeposit)}
              </span>
            </div>
            <Separator />
            <div className="flex justify-between">
              <span className="text-muted-foreground">Payment Due Day</span>
              <span className="font-medium">Day {lease.paymentDueDay}</span>
            </div>
            <Separator />
            <div className="flex justify-between">
              <span className="text-muted-foreground">Late Fee</span>
              <span className="font-medium">
                {formatNaira(lease.lateFeeAmount)}
              </span>
            </div>
            <Separator />
            <div className="flex justify-between">
              <span className="text-muted-foreground">Grace Period</span>
              <span className="font-medium">
                {lease.lateFeeGracePeriod} days
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Lease Terms</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Start Date</span>
              <span className="font-medium">
                {new Date(lease.startDate).toLocaleDateString()}
              </span>
            </div>
            <Separator />
            <div className="flex justify-between">
              <span className="text-muted-foreground">End Date</span>
              <span className="font-medium">
                {new Date(lease.endDate).toLocaleDateString()}
              </span>
            </div>
            <Separator />
            <div className="flex justify-between">
              <span className="text-muted-foreground">Termination Notice</span>
              <span className="font-medium">
                {lease.terminationNotice} days
              </span>
            </div>
            <Separator />
            <div className="flex justify-between">
              <span className="text-muted-foreground">Max Occupants</span>
              <span className="font-medium">{lease.maxOccupants}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Rules & Restrictions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Pets Allowed</span>
              <Badge variant={lease.petsAllowed ? "default" : "secondary"}>
                {lease.petsAllowed ? "Yes" : "No"}
              </Badge>
            </div>
            <Separator />
            <div className="flex justify-between">
              <span className="text-muted-foreground">Smoking Allowed</span>
              <Badge variant={lease.smokingAllowed ? "default" : "secondary"}>
                {lease.smokingAllowed ? "Yes" : "No"}
              </Badge>
            </div>
            <Separator />
            <div className="flex justify-between">
              <span className="text-muted-foreground">Subletting Allowed</span>
              <Badge
                variant={lease.sublettingAllowed ? "default" : "secondary"}
              >
                {lease.sublettingAllowed ? "Yes" : "No"}
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Signatures</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Landlord</span>
                <Badge variant={lease.landlordSigned ? "success" : "warning"}>
                  {lease.landlordSigned ? "Signed" : "Pending"}
                </Badge>
              </div>
              {lease.landlordSignedAt && (
                <p className="text-sm text-muted-foreground">
                  Signed on{" "}
                  {new Date(lease.landlordSignedAt).toLocaleDateString()}
                </p>
              )}
            </div>
            <Separator />
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Tenant</span>
                <Badge variant={lease.tenantSigned ? "success" : "warning"}>
                  {lease.tenantSigned ? "Signed" : "Pending"}
                </Badge>
              </div>
              {lease.tenantSignedAt && (
                <p className="text-sm text-muted-foreground">
                  Signed on{" "}
                  {new Date(lease.tenantSignedAt).toLocaleDateString()}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Responsibilities */}
      {(lease.utilitiesIncluded?.length > 0 ||
        lease.tenantResponsibilities?.length > 0 ||
        lease.landlordResponsibilities?.length > 0) && (
        <Card>
          <CardHeader>
            <CardTitle>Utilities & Responsibilities</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {lease.utilitiesIncluded?.length > 0 && (
              <div>
                <h4 className="font-medium mb-2">Utilities Included</h4>
                <ul className="list-disc list-inside space-y-1">
                  {lease.utilitiesIncluded.map((utility, index) => (
                    <li key={index} className="text-muted-foreground">
                      {utility}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {lease.tenantResponsibilities?.length > 0 && (
              <div>
                <h4 className="font-medium mb-2">Tenant Responsibilities</h4>
                <ul className="list-disc list-inside space-y-1">
                  {lease.tenantResponsibilities.map((resp, index) => (
                    <li key={index} className="text-muted-foreground">
                      {resp}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {lease.landlordResponsibilities?.length > 0 && (
              <div>
                <h4 className="font-medium mb-2">Landlord Responsibilities</h4>
                <ul className="list-disc list-inside space-y-1">
                  {lease.landlordResponsibilities.map((resp, index) => (
                    <li key={index} className="text-muted-foreground">
                      {resp}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Renewal Terms */}
      {lease.renewalTerms && (
        <Card>
          <CardHeader>
            <CardTitle>Renewal Terms</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">{lease.renewalTerms}</p>
          </CardContent>
        </Card>
      )}

      {/* Sign Lease Dialog */}
      <Dialog open={showSignDialog} onOpenChange={setShowSignDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Sign Lease Agreement</DialogTitle>
            <DialogDescription>
              By signing this lease agreement, you confirm that you have read
              and agree to all the terms and conditions.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Your Full Name</label>
              <Textarea
                placeholder="Type your full name to sign"
                value={signature}
                onChange={(e) => setSignature(e.target.value)}
                rows={2}
              />
              <p className="text-xs text-muted-foreground">
                This constitutes your electronic signature
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowSignDialog(false)}
              disabled={signing}
            >
              Cancel
            </Button>
            <Button onClick={handleSignLease} disabled={signing || !signature}>
              {signing ? "Signing..." : "Sign Lease"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Email Dialog */}
      <Dialog open={showSendEmailDialog} onOpenChange={setShowSendEmailDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Lease to Email</DialogTitle>
            <DialogDescription>
              Send this lease agreement to the tenant's email address
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Recipient</label>
              <p className="text-sm text-muted-foreground">
                {lease.tenantName || "Tenant"}
              </p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Message (Optional)</label>
              <Textarea
                placeholder="Add a personal message..."
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowSendEmailDialog(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleSendEmail}>
              <Mail className="h-4 w-4 mr-2" />
              Send Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Terminate Lease Dialog */}
      <Dialog open={showTerminateDialog} onOpenChange={setShowTerminateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Terminate Lease Agreement</DialogTitle>
            <DialogDescription>
              This action will terminate the lease agreement. Please provide a
              reason for termination.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-destructive/10 border border-destructive/20 rounded-md p-3 flex gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
              <p className="text-sm text-destructive">
                Warning: This action cannot be undone. The tenant will be
                notified of the termination.
              </p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Termination Reason</label>
              <Textarea
                placeholder="Enter reason for termination..."
                value={terminationReason}
                onChange={(e) => setTerminationReason(e.target.value)}
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowTerminateDialog(false)}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleTerminateLease}>
              <XCircle className="h-4 w-4 mr-2" />
              Terminate Lease
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
